/**
 * TakeTourPill — the always-available "Take a tour" entry point.
 *
 * Mounted in the screen's header chrome. Tapping fires an auto-walk
 * through the provided beacon sequence. Persists nothing — pure CTA.
 *
 * Visual: small rosegold pill with Maya's avatar + "Take a tour".
 * Tone matches AutoSavedPill / AnonSaveStatusPill so it lives
 * comfortably in the same header strip.
 */
import { Sparkles } from "lucide-react";

import useTour from "./useTour";
import MayaAvatar from "../../pages/shop/custom-studio/v4/components/MayaAvatar";

export default function TakeTourPill({
    sequence = [],
    label = "Take a quick tour",
    dense = false,
    testid = "take-tour-pill",
}) {
    const { startAutoWalk } = useTour();
    if (!Array.isArray(sequence) || !sequence.length) return null;

    function handleClick() {
        startAutoWalk(sequence, { force: true });
    }

    return (
        <button
            type="button"
            onClick={handleClick}
            className={`inline-flex items-center gap-1.5 rounded-full border bg-gradient-to-r from-rosegold-mist/60 via-amber-50 to-rosegold-mist/40 hover:from-rosegold-mist/80 hover:to-rosegold-mist/60 border-rosegold/40 hover:border-rosegold-deep text-rosegold-deep hover:text-ink shadow-sm transition-all ${
                dense ? "h-7 pl-1 pr-2.5 text-[10.5px]" : "h-9 pl-1.5 pr-3.5 text-[11.5px]"
            } font-bold tracking-wide`}
            data-testid={testid}
            title="A quick guided walk through this screen with Maya"
        >
            <span className="inline-flex">
                <MayaAvatar size="xs" sparkle testid="take-tour-pill-avatar" />
            </span>
            <span className="hidden xs:inline">{label}</span>
            <span className="xs:hidden">Tour</span>
            <Sparkles size={dense ? 9 : 10} />
        </button>
    );
}
